import { FORMAT } from 'common/constants';
import dayjs from 'dayjs';
import {
    ICampaignResponse,
    ICampaignType,
    ICampaignQuestionDetail,
    ICampaignOverview,
    ICampaignOverviewResponse,
} from 'types/campaign';
import { IQuestionNewEditForm } from 'types/campaign-design';
import CampaignConstants from 'pages/admin/campaign/constants';
const questionAnswerType = CampaignConstants.questionAnswerType;

export const campaignsResponseFormatter = (
    campaign: ICampaignResponse,
    t: CallableFunction
) => {
    const outdated = dayjs(campaign.attributes.close_date).isBefore(dayjs());
    const createdDate = dayjs(campaign.attributes.created_at).format(
        FORMAT.date.DMY
    );
    const item: ICampaignType = {
        id: parseInt(campaign.id),
        campaign: campaign.attributes.title,
        questions: campaign.attributes.total_question_count,
        campaignKind: campaign.attributes.service_kind,
        kind: campaign.attributes.kind,
        collectedAmount: campaign.attributes.total_answered || 0,
        totalAmount: campaign.attributes.total_answer_limit || 0,
        timeToCollect: campaign.attributes.campaign_time || '',
        timeStatus: outdated ? t('page.campaign.outdated') : null,
        status_client: campaign.attributes.status_client || '',
        creator: campaign.attributes.creator_name,
        createdDate,
        actions: '',
        uid: campaign.attributes.uid,
        description: campaign.attributes.description,
        editable: campaign.attributes.editable,
        inPublicTime: campaign.attributes.in_public_time,
        code: campaign.attributes.code,
        uuid: campaign.attributes.uuid,
        paymentStatus: campaign.attributes.payment_status,
        is_unique_link: campaign.attributes.is_unique_link,
        enable_minimum_answer_time:
            campaign.attributes.enable_minimum_answer_time,
        minimum_answer_time: campaign.attributes.minimum_answer_time,
        secure_type: campaign.attributes.secure_type,
        is_multiple_answer: campaign.attributes.is_multiple_answer,
    };
    return item;
};

export const campaignsOverviewResponseFormatter = (
    campaign: ICampaignOverviewResponse
) => {
    const item: ICampaignOverview = {
        cardInformation: campaign.attributes.card_information,
        chartDevice: campaign.attributes.chart_device,
        chartArea: campaign.attributes.chart_area,
        chartDataCollected: campaign.attributes.chart_data_collected,
    };
    return item;
};

export const checkMentionAndFormatQuestion = (
    question: IQuestionNewEditForm
) => {
    if (
        question.questionIdForTitle &&
        question.questionIdForTitle > 0 &&
        question.questionTitleLogic
    ) {
        const originalString = question.question;
        const start = question.mentionStartPos || 0;
        const end = question.mentionEndPos || 0;
        const replacement = `\${${question.questionTitleLogic.question}}`;

        const newString =
            originalString.slice(0, start) +
            replacement +
            originalString.slice(end);
        return newString;
    } else {
        return question.question;
    }
};

export const logicDisplayAnswerOnTitlteTransformMarkUp = (display: string) => {
    return `$title"${display}"`;
};

export const resetValuesWhenChangeQuestionType = (
    question: IQuestionNewEditForm,
    questionType: string
) => {
    const resetParams: IQuestionNewEditForm = {
        id: question.id,
        question: checkMentionAndFormatQuestion(question),
        questionType,
        data: null,
        required: question.required || false,
        questionNumber: question.questionNumber || 1,
        questionIdForTitle: 0,
        mentionStartPos: 0,
        mentionEndPos: 0,
        mentionValue: '',
    };
    if (questionType === question.questionType) {
        resetParams.data = question.data;
    } else if (questionType === 'textbox') {
        resetParams.data = null;
    } else if (
        questionType === 'checkbox' ||
        questionType === 'single_choice'
    ) {
        resetParams.data = {
            answers: [
                {
                    id: '',
                    answer: 'Tuỳ chọn 1',
                    type: questionAnswerType.NORMAL_ANSWER,
                },
            ],
            numberOfRows: 1,
            addAnotherAnswer: false,
            addNoAnswersYet: false,
            insertImage: false,
            insertMultipleAnswers: false,
            sortAnswers: 'default',
            minimumAnswerChoice: 1,
            maximumAnswerChoice: 1,
            anotherAnswerText: '',
        };
    } else if (
        questionType === 'score_star_full' ||
        questionType === 'score_star_half'
    ) {
        resetParams.data = {
            answers: [{ answer: 'Tùy chọn 1', score: 1 }],
            numberOfRows: 1,
            quantity: 5,
        };
    } else if (questionType === 'score') {
        resetParams.data = {
            answers: [{ answer: 'Tùy chọn 1', score: 1 }],
            numberOfRows: 1,
            scale: 100,
            gap: 10,
        };
    } else if (questionType === 'dropdown') {
        resetParams.data = {
            answers: [{ answer: 'Tùy chọn 1' }],
            numberOfRows: 1,
            sortAnswers: 'default',
        };
    } else if (questionType === 'image') {
        resetParams.data = {
            limitSize: 20,
        };
    } else if (questionType === 'image_with_title') {
        resetParams.data = {
            answers: [{ answer: 'Tùy chọn 1' }],
            numberOfRows: 1,
            limitSize: 20,
        };
    } else if (questionType === 'textbox_recommend') {
        resetParams.data = {
            answers: [{ answer: 'Tùy chọn 1' }],
            numberOfRows: 1,
            limitCharacter: 20,
        };
    } else if (questionType === 'emoji') {
        resetParams.data = {
            answers: [{ answer: 'Tùy chọn 1' }],
            numberOfRows: 1,
            numberOfEmoji: 2,
        };
    } else if (questionType === 'textbox_recommend_multi_choice') {
        resetParams.data = {
            answers: [{ answer: 'Tùy chọn 1' }],
            numberOfRows: 1,
            minimumAnswerChoice: 1,
            maximumAnswerChoice: 1,
        };
    } else if (questionType === 'rank') {
        resetParams.data = {
            answers: [{ answer: 'Tùy chọn 1', rank: 1 }],
            numberOfRows: 1,
            sortAnswers: 'default',
        };
    } else if (questionType === 'slider_scale') {
        resetParams.data = {
            answers: [{ answer: 'Tùy chọn 1', score: 10 }],
            numberOfRows: 1,
            scale: 100,
            gap: 10,
        };
    } else if (questionType === 'matrix_text') {
        resetParams.data = {
            rows: [{ label: 'Tùy chọn hàng 1' }],
            columns: [{ label: 'Tùy chọn cột 1' }],
            numberOfRows: 1,
            numberOfColumns: 1,
            insertMultipleAnswers: false,
        };
    } else if (questionType === 'matrix_checkbox') {
        resetParams.data = {
            rows: [{ label: 'Tùy chọn hàng 1' }],
            columns: [{ label: 'Tùy chọn cột 1' }],
            numberOfRows: 1,
            numberOfColumns: 1,
            minimumAnswerChoice: 1,
            maximumAnswerChoice: 1,
            direction: 'row',
            sortAnswers: 'default',
            insertMultipleAnswers: false,
        };
    } else if (questionType === 'matrix_radio') {
        resetParams.data = {
            rows: [{ label: 'Tùy chọn hàng 1' }],
            columns: [{ label: 'Tùy chọn cột 1' }],
            numberOfRows: 1,
            numberOfColumns: 1,
            direction: 'row',
            sortAnswers: 'default',
            insertMultipleAnswers: false,
        };
    }
    return resetParams;
};

export const listCampaignQuestionsFormatter = (
    questionRes: ICampaignQuestionDetail
) => {
    const {
        id,
        title,
        kind,
        answer_options,
        min_choice,
        max_choice,
        order_by,
        // min_score,
        max_score,
        matrix_answer_type,
        number_of_stars,
        slider_max,
        // slider_min,
        slider_step,
        number_emoji,
        limit_size,
        limit_character,
        table_question_component,
        required,
        number,
        score_step,
        skip_logic,
        creening_logic,
        question_title_logic,
        answer_from_other_question_logic,
        branch_logic,
        setting_logic,
        is_page_config,
    } = questionRes.attributes;
    let questionData: any = null; // eslint-disable-line @typescript-eslint/no-explicit-any -- Can be any type of data
    switch (kind) {
        case 'checkbox': {
            let checkAddAnotherAnswer = false;
            let checkAddNoAnswersYet = false;
            let checkAnotherAnswerText = '';
            const answersFormatted = answer_options.map((item) => {
                if (item.ordinal === 1) {
                    checkAddAnotherAnswer = true;
                    checkAnotherAnswerText = item.text;
                }
                if (item.ordinal === 2) {
                    checkAddNoAnswersYet = true;
                }
                return {
                    id: item.id,
                    answer: item.text,
                    type:
                        item.ordinal === 0
                            ? questionAnswerType.NORMAL_ANSWER
                            : item.ordinal === 1
                              ? questionAnswerType.OTHER_ANSWER
                              : questionAnswerType.NO_ANSWER,
                    image: item.image,
                    imageURL: item.image,
                    question_page_id: item.question_page_id,
                };
            });
            questionData = {
                answers: answersFormatted,
                numberOfRows: answersFormatted.filter(
                    (item) => item.type === questionAnswerType.NORMAL_ANSWER
                ).length,
                insertImage: !!answersFormatted.find((item) => item.imageURL),
                insertMultipleAnswers: false,
                sortAnswers: order_by || 'default',
                minimumAnswerChoice: min_choice,
                maximumAnswerChoice: max_choice,
                addAnotherAnswer: checkAddAnotherAnswer,
                addNoAnswersYet: checkAddNoAnswersYet,
                anotherAnswerText: checkAnotherAnswerText,
            };
            break;
        }
        case 'single_choice': {
            let checkAddAnotherAnswer = false;
            let checkAddNoAnswersYet = false;
            let checkAnotherAnswerText = '';
            const answersFormatted = answer_options.map((item) => {
                if (item.ordinal === 1) {
                    checkAddAnotherAnswer = true;
                    checkAnotherAnswerText = item.text;
                }
                if (item.ordinal === 2) {
                    checkAddNoAnswersYet = true;
                }
                return {
                    id: item.id,
                    answer: item.text,
                    type:
                        item.ordinal === 0
                            ? questionAnswerType.NORMAL_ANSWER
                            : item.ordinal === 1
                              ? questionAnswerType.OTHER_ANSWER
                              : questionAnswerType.NO_ANSWER,
                    image: item.image,
                    imageURL: item.image,
                    question_page_id: item.question_page_id,
                };
            });
            questionData = {
                answers: answersFormatted,
                numberOfRows: answersFormatted.filter(
                    (item) => item.type === questionAnswerType.NORMAL_ANSWER
                ).length,
                insertImage: !!answersFormatted.find((item) => item.imageURL),
                insertMultipleAnswers: false,
                sortAnswers: order_by || 'default',
                addAnotherAnswer: checkAddAnotherAnswer,
                addNoAnswersYet: checkAddNoAnswersYet,
                anotherAnswerText: checkAnotherAnswerText,
            };
            break;
        }
        case 'score_star_full': {
            const answersFormatted = answer_options.map((item) => {
                return {
                    id: item.id,
                    answer: item.text,
                };
            });
            questionData = {
                answers: answersFormatted,
                numberOfRows: answersFormatted.length,
                quantity: number_of_stars,
            };
            break;
        }
        case 'score_star_half': {
            const answersFormatted = answer_options.map((item) => {
                return {
                    id: item.id,
                    answer: item.text,
                };
            });
            questionData = {
                answers: answersFormatted,
                numberOfRows: answersFormatted.length,
                quantity: number_of_stars,
            };
            break;
        }
        case 'score': {
            questionData = {
                scale: max_score,
                gap: score_step,
                answers: [{ id: 1, answer: '' }], // Dummy data
            };
            break;
        }
        case 'dropdown': {
            const answersFormatted = answer_options.map((item) => {
                return {
                    id: item.id,
                    answer: item.text,
                };
            });
            questionData = {
                answers: answersFormatted,
                numberOfRows: answersFormatted.length,
                sortAnswers: order_by || 'default',
            };
            break;
        }
        case 'image': {
            questionData = {
                limitSize: limit_size,
            };
            break;
        }
        case 'image_with_title': {
            const answersFormatted = answer_options.map((item) => {
                return {
                    id: item.id,
                    answer: item.text,
                };
            });
            questionData = {
                answers: answersFormatted,
                numberOfRows: answersFormatted.length,
                limitSize: limit_size,
            };
            break;
        }
        case 'emoji': {
            const answersFormatted = answer_options.map((item) => {
                return {
                    id: item.id,
                    answer: item.text,
                };
            });
            questionData = {
                answers: answersFormatted,
                numberOfRows: answersFormatted.length,
                numberOfEmoji: number_emoji,
            };
            break;
        }
        case 'textbox_recommend': {
            const answersFormatted = answer_options.map((item) => {
                return {
                    id: item.id,
                    answer: item.text,
                };
            });
            questionData = {
                answers: answersFormatted,
                numberOfRows: answersFormatted.length,
                limitCharacter: limit_character,
            };
            break;
        }
        case 'textbox_recommend_multi_choice': {
            const answersFormatted = answer_options.map((item) => {
                return {
                    id: item.id,
                    answer: item.text,
                };
            });
            questionData = {
                answers: answersFormatted,
                numberOfRows: answersFormatted.length,
            };
            break;
        }
        case 'rank': {
            const answersFormatted = answer_options.map((item) => {
                return {
                    id: item.id,
                    answer: item.text,
                };
            });
            questionData = {
                answers: answersFormatted,
                numberOfRows: answersFormatted.length,
                sortAnswers: order_by || 'default',
            };
            break;
        }
        case 'slider_scale': {
            const answersFormatted = answer_options.map((item) => {
                return {
                    id: item.id,
                    answer: item.text,
                };
            });
            questionData = {
                answers: answersFormatted,
                numberOfRows: answersFormatted.length,
                scale: slider_max,
                gap: slider_step,
            };
            break;
        }
        case 'matrix_text': {
            const formatRows = table_question_component.rows.map((item) => {
                return {
                    label: item,
                };
            });
            const formatColumns = table_question_component.columns.map(
                (item) => {
                    return {
                        label: item,
                    };
                }
            );
            questionData = {
                rows: formatRows,
                columns: formatColumns,
                numberOfRows: formatRows.length,
                numberOfColumns: formatColumns.length,
                insertMultipleAnswers: false,
            };
            break;
        }
        case 'matrix_checkbox': {
            const formatRows = table_question_component.rows.map((item) => {
                return {
                    label: item,
                };
            });
            const formatColumns = table_question_component.columns.map(
                (item) => {
                    return {
                        label: item,
                    };
                }
            );
            questionData = {
                rows: formatRows,
                columns: formatColumns,
                numberOfRows: formatRows.length,
                numberOfColumns: formatColumns.length,
                direction: matrix_answer_type,
                sortAnswers: order_by || 'default',
                minimumAnswerChoice: min_choice,
                maximumAnswerChoice: max_choice,
                insertMultipleAnswers: false,
            };
            break;
        }
        case 'matrix_radio': {
            const formatRows = table_question_component.rows.map((item) => {
                return {
                    label: item,
                };
            });
            const formatColumns = table_question_component.columns.map(
                (item) => {
                    return {
                        label: item,
                    };
                }
            );
            questionData = {
                rows: formatRows,
                columns: formatColumns,
                numberOfRows: formatRows.length,
                numberOfColumns: formatColumns.length,
                direction: matrix_answer_type,
                sortAnswers: order_by || 'default',
                insertMultipleAnswers: false,
            };
            break;
        }
        default:
            questionData = null;
    }
    const item: IQuestionNewEditForm = {
        id,
        question: title,
        questionType: kind,
        data: questionData,
        required,
        setting_logic,
        questionNumber: number,
        skipLogic: skip_logic,
        creeningLogic: creening_logic,
        questionTitleLogic: question_title_logic,
        answerFromOtherQuestionLogic: answer_from_other_question_logic,
        branchLogic: branch_logic,
        isPageConfig: is_page_config,
    };
    if (question_title_logic && question_title_logic.id) {
        item.questionIdForTitle = question_title_logic.mention_question_id;
        item.mentionStartPos = question_title_logic.start_pos;
        item.mentionEndPos = question_title_logic.end_pos;
    }
    return item;
};
